import { RacemapColors } from '@racemap/utilities/consts/common';
import type { PointWithSpeedAndOffset } from '@racemap/utilities/functions/timing';
import type { EventTrackObject } from '@racemap/utilities/types/geos';
import type { Point } from '@racemap/utilities/types/types';
import type { Feature, FeatureCollection, LineString, Polygon, Position } from 'geojson';
import type { Immutable } from 'immer';
import { type FC, useContext, useEffect, useId, useMemo } from 'react';
import { Layer, type MapMouseEvent, Source } from 'react-map-gl/mapbox';
import { MapContext, type NewDataProps } from '../BasicMap/BasicMap';
import {
  type AUTOFOCUS,
  convertTracksToFeatures,
  getPointPaint,
  injectFeatureIds,
} from '../helpers';
import { RadiusLayer } from './RadiusLayer';

export type LineStringProperties =
  | (Record<string, unknown> & {
      lineWidth?: number;
      color?: string;
    })
  | null;

export type Track =
  | null
  | Array<Position>
  | Array<PointWithSpeedAndOffset>
  | Array<Point>
  | Immutable<Array<Position>>
  | EventTrackObject
  | Feature<LineString, LineStringProperties>
  | Immutable<Feature<LineString, LineStringProperties>>
  | FeatureCollection<LineString, LineStringProperties>
  | Immutable<FeatureCollection<LineString, LineStringProperties>>;

export interface Props {
  tracks: Array<Track | undefined> | undefined;
  color?: string;
  fillColor?: string;
  lineWidth?: number;
  showPoints?: boolean;
  radius?: number;
  onNewData?: (props: NewDataProps) => void;
  autofocus?: boolean | AUTOFOCUS;
  radiusFillColor?: string;
  onHover?: (evt: MapMouseEvent) => void;
  onMouseEnter?: (evt: MapMouseEvent) => void;
  onMouseLeave?: (evt: MapMouseEvent) => void;
  id?: string;
  beforeId?: string;
}

export const TrackLayer: FC<Props> = ({
  tracks,
  color,
  fillColor = RacemapColors.CloudBlue,
  lineWidth,
  showPoints = false,
  radius = null,
  onNewData,
  autofocus = false,
  radiusFillColor,
  onMouseEnter,
  onMouseLeave,
  id: settedId,
  beforeId,
}) => {
  const genId = useId();
  const id = settedId || genId;
  const { setMouseEnterAction, setMouseLeaveAction, addInteractiveLayerIds } =
    useContext(MapContext);

  const { features, paint } = useMemo(
    () => convertTracksToFeatures(tracks, color, lineWidth),
    [tracks, color, lineWidth],
  );
  const featuresWithIdProperty = useMemo(() => injectFeatureIds(features), [features]);
  const featuresCollection: FeatureCollection<LineString | Polygon, LineStringProperties> = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: [...featuresWithIdProperty],
    }),
    [featuresWithIdProperty],
  );

  useEffect(() => {
    addInteractiveLayerIds(`layer-line-${id}`);
    if (showPoints) {
      addInteractiveLayerIds(`layer-circles-${id}`);
    }
  }, [showPoints]);

  useEffect(() => {
    onNewData?.({
      layer: id,
      data: featuresCollection,
      autofocus,
    });
  }, [autofocus, id, featuresCollection, onNewData]);

  useEffect(() => {
    if (onMouseEnter != null) {
      setMouseEnterAction(id, onMouseEnter);
    }
    if (onMouseLeave != null) {
      setMouseLeaveAction(id, onMouseLeave);
    }
  }, [onMouseEnter, onMouseLeave]);

  return (
    <>
      {radius != null && (
        <RadiusLayer
          features={features}
          radius={radius}
          fillColor={radiusFillColor}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      )}
      <Source type="geojson" data={featuresCollection} id={id} key={id} promoteId="_id">
        <Layer
          id={`layer-line-${id}`}
          key={`layer-line-${id}`}
          type="line"
          paint={paint}
          beforeId={beforeId}
        />
        {showPoints && (
          <Layer
            id={`layer-circles-${id}`}
            key={`layer-circles-${id}`}
            type="circle"
            paint={getPointPaint(paint, fillColor)}
          />
        )}
      </Source>
    </>
  );
};
